import React, { useEffect } from "react";
import axios from "axios";
import { useState } from "react";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { ClipLoader } from "react-spinners";

const Login = ({ setLoggedInUser }) => {
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const logout = async () => {
            try {
                await axios.get(
                    `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/api/v1/user/logout`,
                    { withCredentials: true }
                );
                setLoggedInUser(null)
            } catch (error) {
                toast.error("Failed to logout")
            }
        };

        logout();
    }, [setLoggedInUser]);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const navigate = useNavigate();

    const onSubmit = async (data) => {
        try {
            setLoading((prev) => !prev);
            let response = await axios.post(
                `${import.meta.env.VITE_REACT_APP_BACKEND_BASEURL}/api/v1/user/login`,
                data,
                { withCredentials: true }
            );

            setLoggedInUser(response.data.user);
            setLoading((prev) => !prev);
            navigate("/");
        } catch (error) {
            setLoading(false);
            if (error.response && error.response.data) {
                toast.error(error.response.data.message);
            }
        }
    };

    const handleForgotPassword = () => {
        navigate("/forgot-password")
    }

    const submitForm = handleSubmit(onSubmit);

    return (
        <div className="min-h-screen flex flex-col items-center justify-center p-4 sm:p-8 gap-8 sm:gap-16 w-[90%] mx-auto">
            {/* Logo */}
            <div className="w-full sm:w-auto flex justify-center sm:justify-start self-start">
                <img
                    src="/images/Powersolv.png"
                    alt="Powersolv logo"
                    className="w-28 sm:w-32 xl:w-36 2xl:w-40"
                />
            </div>

            {/* Content Container */}
            <div className="flex flex-col text-center sm:text-left sm:flex-row sm:items-center sm:justify-between gap-8 sm:gap-16">
                {/* Left Column - Text and Form */}
                <div className="w-full sm:w-1/2 flex flex-col order-2 sm:order-1">
                    <h2 className="font-bold text-3xl sm:text-4xl xl:text-5xl 2xl:text-6xl leading-none text-[#87213C] mb-4 sm:mb-6 ">
                        Powersolv PowerHires
                    </h2>

                    {/* Mobile-only image */}
                    <div className="sm:hidden flex justify-center mb-6">
                        <img
                            src="/images/LoginMain.png"
                            alt="Login illustration"
                            className="w-full max-w-xs"
                        />
                    </div>

                    <p className="text-sm sm:text-base xl:text-lg mb-6 sm:mb-8 font-medium ">
                        Welcome to PowerHires – the ultimate recruitment tool designed to simplify and accelerate your hiring process.
                    </p>

                    <form
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                submitForm();
                            }
                        }}
                        className="w-full max-w-md"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="mb-4">
                            <input
                                aria-label="Email"
                                type="text"
                                id="email"
                                {...register("email", { required: "Email is required" })}
                                className={`w-full px-4 py-3 rounded-lg border ${errors.email ? "border-red-500" : "border-[#797979]"} text-sm sm:text-base`}
                                placeholder="Email"
                            />
                            {errors.email && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.email.message}
                                </p>
                            )}
                        </div>

                        <div className="mb-4">
                            <input
                                aria-label="Password"
                                autoComplete="current-password"
                                type="password"
                                id="password"
                                {...register("password", { required: "Password is required" })}
                                className={`w-full px-4 py-3 rounded-lg border ${errors.password ? "border-red-500" : "border-[#797979]"} text-sm sm:text-base`}
                                placeholder="Password"
                            />
                            {errors.password && (
                                <p className="text-red-500 text-xs mt-1">
                                    {errors.password.message}
                                </p>
                            )}
                        </div>

                        <button type="button" onClick={handleForgotPassword} className="block text-[12px] hover:underline cursor-pointer mb-2">Forgot Password?</button>

                        <button
                            type="submit"
                            className="w-[70%] bg-[#7F132F] hover:bg-[#55051A] text-white py-3 sm:py-3 rounded-lg font-semibold text-lg transition-colors cursor-pointer"
                        >
                            {loading ? <ClipLoader size={"1em"} color="white" /> : "Login"}
                        </button>
                    </form>
                </div>

                {/* Right Column - Image (hidden on mobile, shown on sm and above) */}
                <div className="hidden sm:block sm:w-1/2 flex justify-end order-1 sm:order-2">
                    <img
                        src="/images/LoginMain.png"
                        alt="Login illustration"
                        className="w-full max-w-md sm:max-w-lg xl:max-w-xl 2xl:max-w-2xl"
                    />
                </div>
            </div>
        </div>
    );
};

export default Login;
